import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Grid2, Paper, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';

import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext.tsx';
import { FormField, FormFieldType } from '@enroller/admissions-components';
import { usePageTitle } from '@enroller/utils';
import { AgreementsFormValues, agreementsSchema, defaultValues } from './AgreementsFormValues.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useApplicationContext } from '../../../contexts/ApplicationContext/ApplicationContext.tsx';

export const AgreementsForm = () => {
  const { setSubmit, setHeaderTitle, setIsFormLoading } = useHeaderContext();
  const { setPageTitle } = usePageTitle();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');
  const { setApplicationPageData, triggerErrors, displayErrors } = useApplicationContext();

  useEffect(() => {
    setHeaderTitle({ title: 'Agreements', subtitle: 'Conditions of Participation' });
    setPageTitle('Agreements');
  }, [setHeaderTitle, setPageTitle]);

  const resolver = useMemo(() => {
    const currentSchema = validationMode === ValidationType.Full ? agreementsSchema.required : agreementsSchema.optional;
    return zodResolver(currentSchema);
  }, [validationMode]);

  const { control, handleSubmit, trigger } = useForm<AgreementsFormValues>({
    defaultValues,
    resolver,
  });

  // Trigger errors if they have been triggered in another form
  useEffect(() => {
    if (displayErrors) setValidationMode(ValidationType.Full);
  }, [displayErrors, trigger]);

  const onSubmit = useCallback(
    () =>
      handleSubmit(
        (data) => {
          setApplicationPageData('agreements', data);
          if (validationMode === ValidationType.Full) triggerErrors();
          setIsFormLoading(true);
          setTimeout(() => {
            console.log('Form submitted:', data);
            setIsFormLoading(false);
          }, 1000);
        },
        () => triggerErrors(),
      )(),
    [handleSubmit, setIsFormLoading, setApplicationPageData, triggerErrors, validationMode],
  );

  // Submit form when resolver is changed.
  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);
  return (
    <Stack component="form" onSubmit={onSubmit} gap={3}>
      <DocumentSectionWrapper>
        <Grid2 size={12}>
          <Typography variant="subtitle1">Agreements</Typography>
        </Grid2>

        <Grid2 size={12} container direction="row" alignItems="center" gap={2}>
          <Typography variant="subtitle2">Conditions of Participation</Typography>

          <Button onClick={() => console.log('Resend Email for Online Signature')} variant="outlined" color="primary">
            Resend Email for Online Signature
          </Button>
          <Button onClick={() => console.log('Download Blank Form')} variant="outlined" color="primary">
            Download Blank Form
          </Button>
        </Grid2>

        <Grid2 size={12}>
          <FormField
            label=""
            control={control}
            id="conditionsParticipationDocumentId"
            name="conditionsParticipationDocument"
            type={FormFieldType.FileUploader}
          />
        </Grid2>
      </DocumentSectionWrapper>

      <DocumentSectionWrapper>
        <Grid2 size={12} container direction="row" alignItems="center" gap={2}>
          <Typography variant="subtitle2">Fee Transparency Acknowledgment</Typography>

          <Button onClick={() => console.log('Resend Email for Online Signature')} variant="outlined" color="primary">
            Resend Email for Online Signature
          </Button>
          <Button onClick={() => console.log('Download Blank Form')} variant="outlined" color="primary">
            Download Blank Form
          </Button>
        </Grid2>

        <Grid2 size={12}>
          <Alert variant="outlined" severity="info">
            <Typography variant="body2">
              The relevant parties have been emailed with a link to sign the form online. You can send a reminder email with the links, or if preferred,
              download a PDF to be signed and uploaded here
            </Typography>
          </Alert>
        </Grid2>

        <Grid2 size={12}>
          <FormField
            label=""
            control={control}
            id="feeTransparencyAcknowledgmentDocumentId"
            name="feeTransparencyAcknowledgmentDocument"
            type={FormFieldType.FileUploader}
          />
        </Grid2>
      </DocumentSectionWrapper>

      <DocumentSectionWrapper>
        <Grid2 size={12} container direction="row" alignItems="center" gap={2}>
          <Typography variant="subtitle2">Medical Release</Typography>

          <Button onClick={() => console.log('Resend Email for Online Signature')} variant="outlined" color="primary">
            Resend Email for Online Signature
          </Button>
          <Button onClick={() => console.log('Download Blank Form')} variant="outlined" color="primary">
            Download Blank Form
          </Button>
        </Grid2>

        <Grid2 size={12}>
          <Alert variant="outlined" severity="info">
            <Typography variant="body2">
              The relevant parties have been emailed with a link to sign the form online. You can send a reminder email with the links, or if preferred,
              download a PDF to be signed and uploaded here
            </Typography>
          </Alert>
        </Grid2>

        <Grid2 size={12}>
          <FormField label="" control={control} id="medicalReleaseDocumentId" name="medicalReleaseDocument" type={FormFieldType.FileUploader} />
        </Grid2>
      </DocumentSectionWrapper>

      <DocumentSectionWrapper>
        <Grid2 size={12} container direction="row" alignItems="center" gap={2}>
          <Typography variant="subtitle2">Permission For Travel (optional)</Typography>

          <Button onClick={() => console.log('Resend Email for Online Signature')} variant="outlined" color="primary">
            Resend Email for Online Signature
          </Button>
          <Button onClick={() => console.log('Download Blank Form')} variant="outlined" color="primary">
            Download Blank Form
          </Button>
        </Grid2>

        <Grid2 size={12}>
          <Alert variant="outlined" severity="info">
            <Typography variant="body2">
              The relevant parties have been emailed with a link to sign the form online. You can send a reminder email with the links, or if preferred,
              download a PDF to be signed and uploaded here
            </Typography>
          </Alert>
        </Grid2>

        <Grid2 size={12}>
          <FormField
            label=""
            control={control}
            id="permissionForTravelOptionalDocumentId"
            name="permissionForTravelOptionalDocument"
            type={FormFieldType.FileUploader}
          />
        </Grid2>
      </DocumentSectionWrapper>

      <DocumentSectionWrapper>
        <Grid2 size={12} container direction="row" alignItems="center" gap={2}>
          <Typography variant="subtitle2">Double Placement Permission</Typography>

          <Button onClick={() => console.log('Resend Email for Online Signature')} variant="outlined" color="primary">
            Resend Email for Online Signature
          </Button>
          <Button onClick={() => console.log('Download Blank Form')} variant="outlined" color="primary">
            Download Blank Form
          </Button>
        </Grid2>

        <Grid2 size={12}>
          <Alert variant="outlined" severity="info">
            <Typography variant="body2">
              The relevant parties have been emailed with a link to sign the form online. You can send a reminder email with the links, or if preferred,
              download a PDF to be signed and uploaded here
            </Typography>
          </Alert>
        </Grid2>

        <Grid2 size={12}>
          <FormField
            label=""
            control={control}
            id="doublePlacementPermissionDocumentId"
            name="doublePlacementPermissionDocument"
            type={FormFieldType.FileUploader}
          />
        </Grid2>
      </DocumentSectionWrapper>

      <DocumentSectionWrapper>
        <Grid2 size={12} container direction="row" alignItems="center" gap={2}>
          <Typography variant="subtitle2">Single Host Permission</Typography>

          <Button onClick={() => console.log('Resend Email for Online Signature')} variant="outlined" color="primary">
            Resend Email for Online Signature
          </Button>
          <Button onClick={() => console.log('Download Blank Form')} variant="outlined" color="primary">
            Download Blank Form
          </Button>
        </Grid2>

        <Grid2 size={12}>
          <Alert variant="outlined" severity="info">
            <Typography variant="body2">
              The relevant parties have been emailed with a link to sign the form online. You can send a reminder email with the links, or if preferred,
              download a PDF to be signed and uploaded here
            </Typography>
          </Alert>
        </Grid2>

        <Grid2 size={12}>
          <FormField label="" control={control} id="singleHostPermissionDocumentId" name="singleHostPermissionDocument" type={FormFieldType.FileUploader} />
        </Grid2>
      </DocumentSectionWrapper>
    </Stack>
  );
};

const DocumentSectionWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <Paper sx={{ p: 2 }}>
      <Grid2 container spacing={3}>
        {children}
      </Grid2>
    </Paper>
  );
};
